import React, { useContext, useCallback, useState, createContext } from 'react'

import without from 'lodash/without'

export interface Toast {
  type: 'success' | 'error' | 'warning' | 'info'
  id?: string
  title?: string
  message: string
}

interface ContextType {
  toasts: Toast[]
  addToast: React.Dispatch<Toast>
  dismissToast: React.Dispatch<Toast>
  clearToasts: React.Dispatch<void>
}

const AppToastContext = createContext<ContextType | undefined>(undefined)

const useToasts = () => {
  const context = useContext(AppToastContext)
  if (!context) {
    throw new Error(`appToastsContext must be used within an AppToastsProvider`)
  }
  return context
}

const AppToastsProvider: React.FC<React.PropsWithChildren<unknown>> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([])

  const addToast = useCallback(
    (newToast: any) => {
      setToasts((prev) => [{ ...newToast, id: prev.length + 1 }, ...prev])
    },
    [setToasts],
  )

  const dismissToast = useCallback((toast: any) => setToasts((prev) => without(prev, toast)), [setToasts])

  const clearToasts = useCallback(() => setToasts([]), [setToasts])

  const value: ContextType = { toasts, addToast, dismissToast, clearToasts }

  return <AppToastContext.Provider value={value}>{children}</AppToastContext.Provider>
}

export { AppToastsProvider, useToasts }
